"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { Agent, Datasource } from "../onboard/types";
import {
  fetchAgents,
  fetchDatasources,
  fetchStaticDatasources,
} from "../utils/api";
import {
  ADScope,
  acquireAccessTokenSilent,
  reauthRedirect,
} from "../auth/authConfig";

// Mirror the auth-race hardening from useAgents: while MSAL silent renewal is
// still in flight, retry token acquisition instead of firing token-less
// management API calls that 401 and empty the onboard lists.
const MAX_AUTH_RETRIES = 5;
const AUTH_RETRY_DELAY_MS = 400;

interface OnboardDataContextType {
  agents: Agent[];
  datasources: Datasource[];
  staticDatasources: Datasource[];
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: (showInactive?: boolean) => Promise<void>;
}

const OnboardDataContext = createContext<OnboardDataContextType>({
  agents: [],
  datasources: [],
  staticDatasources: [],
  isLoaded: false,
  isLoading: false,
  error: null,
  refresh: async () => {},
});

export function OnboardDataProvider({ children }: { children: ReactNode }) {
  const { isReady } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [datasources, setDatasources] = useState<Datasource[]>([]);
  const [staticDatasources, setStaticDatasources] = useState<Datasource[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const getTokenWithRetry = useCallback(async (): Promise<string | null> => {
    for (let attempt = 0; attempt <= MAX_AUTH_RETRIES; attempt += 1) {
      const token = await acquireAccessTokenSilent(ADScope.AISearchApi);
      if (token) {
        return token;
      }
      if (attempt < MAX_AUTH_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, AUTH_RETRY_DELAY_MS),
        );
      }
    }
    return null;
  }, []);

  const refresh = useCallback(
    async (showInactive = false) => {
      try {
        setIsLoading(true);
        setError(null);
        const token = await getTokenWithRetry();
        if (!token) {
          const redirecting = await reauthRedirect(ADScope.AISearchApi);
          if (!redirecting) {
            setError("Failed to load data");
          }
          return;
        }
        const [agentsResponse, datasourcesResponse, staticDs] =
          await Promise.all([
            fetchAgents(showInactive, token),
            fetchDatasources(showInactive, token),
            fetchStaticDatasources(token),
          ]);
        setAgents(agentsResponse.agents);
        setDatasources(datasourcesResponse.datasources);
        setStaticDatasources(staticDs.datasources);
        setIsLoaded(true);
      } catch (err) {
        console.error("Error prefetching onboard data:", err);
        setError("Failed to load data");
      } finally {
        setIsLoading(false);
      }
    },
    [getTokenWithRetry],
  );

  useEffect(() => {
    if (isReady && !hasFetched.current) {
      hasFetched.current = true;
      const startPrefetch = () => refresh();
      if (typeof requestIdleCallback === "function") {
        const id = requestIdleCallback(startPrefetch);
        return () => cancelIdleCallback(id);
      }
      const timeout = setTimeout(startPrefetch, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isReady, refresh]);

  return (
    <OnboardDataContext.Provider
      value={{
        agents,
        datasources,
        staticDatasources,
        isLoaded,
        isLoading,
        error,
        refresh,
      }}
    >
      {children}
    </OnboardDataContext.Provider>
  );
}

export function useOnboardData() {
  return useContext(OnboardDataContext);
}
